import isFunction from 'lodash-es/isFunction';
import moment from 'moment';
import { shortDateFormat, dateParseFormats } from '@shared/utils/datetime';

/**
 * inputDateDirective
 *
 * @author Dec Norton <decnorton@gmail.com>
 */

inputDateDirective.$inject = ['$util'];

export function inputDateDirective($util) {
    return {
        // language=HTML
        template: `
            <md-input-container>
                <label>{{ label }}</label>

                <md-datepicker name="{{ name || '' }}"
                               ng-model="date"
                               ng-change="_onDateChanged($event)"
                               md-placeholder="{{ placeholder || dateFormatPlaceholder }}"
                               md-min-date="minDate"
                               md-max-date="maxDate"
                               md-hide-icons="calendar"
                               md-mode="{{ mdMode }}"
                               md-date-filter="mdDateFilter"
                               md-hide-icons="calendar"
                               ng-disabled="disabled"
                               ng-required="required">
                </md-datepicker>

                <div class="hint" ng-show="hint" ng-if="!form[name].$invalid">
                    {{ hint }}
                </div>

                <div ng-if="form && name" ng-messages="form[name].$error">
                    <div ng-message="required">Required</div>
                    <div ng-repeat="(key, message) in errorMessages" ng-message="{{ key }}">{{ message }}</div>
                </div>
            </md-input-container>
        `,
        scope: {
            after: '=?after',
            before: '=?before',
            max: '=?max',
            min: '=?min',
            dispatchChange: '&?change',
            disabled: '=ngDisabled',
            errorMessages: '=errorMessages',
            mdHideIcons: '@?mdHideIcons',
            hint: '@?hint',
            label: '@?label',
            mdMode: '@?mdMode',
            form: '=form',
            mdDateFilter: '=?mdDateFilter',
            name: '@name',
            required: '=?required',
            value: '=?value',
            valueDate: '=?valueDate',
        },
        link: {
            pre: function preLink(scope, element, attrs) {
                element.addClass('app-form-field');

                const hasMinAttr = !!attrs.min;
                const hasMaxAttr = !!attrs.max;

                scope.date = null;
                scope.minDate = null;
                scope.dateFormatPlaceholder = shortDateFormat();
                scope._onDateChanged = _onDateChanged;

                // If it's undefined on the scope, it may mean that the attribute is there but
                // without content. If the attribute isn't undefined then we know it's been added.
                if (scope.required === undefined && attrs.required !== undefined) {
                    scope.required = true;
                }

                // Deal with initialised value
                if (scope.value) {
                    const m = moment(scope.value);

                    if (m.isValid()) {
                        scope.date = m.toDate();

                        _onDateChanged();
                    }
                }

                // Watch for any changes to valueDate
                scope.$watch('value', _valueWatcher);
                scope.$watch('valueDate', valueDateWatcher);
                scope.$watch('after', $util.nextTick(_afterWatcher), true);
                scope.$watch('before', $util.nextTick(_beforeWatcher), true);
                scope.$watch('min', $util.nextTick(_minWatcher), true);
                scope.$watch('max', $util.nextTick(_maxWatcher), true);

                function _valueWatcher(value) {
                    if (value) {
                        scope.date = value;
                    }
                }

                function _afterWatcher(value) {
                    if (hasMinAttr) {
                        return;
                    }

                    if (value) {
                        const m = moment(value);

                        if (m.isValid()) {
                            scope.minDate = m.add(1, 'day').toDate();

                            return;
                        }
                    }

                    scope.minDate = null;
                }

                function _beforeWatcher(value) {
                    if (hasMaxAttr) {
                        return;
                    }

                    if (value) {
                        const m = moment(value);

                        if (m.isValid()) {
                            scope.maxDate = m.subtract(1, 'day').toDate();
                            return;
                        }
                    }

                    scope.maxDate = null;
                }

                function _minWatcher(value) {
                    if (!hasMinAttr) {
                        return;
                    }

                    if (value) {
                        const m = moment(value);

                        if (m.isValid()) {
                            return (scope.minDate = m.toDate());
                        }
                    }

                    scope.minDate = null;
                }

                function _maxWatcher(value) {
                    if (!hasMaxAttr) {
                        return;
                    }

                    if (value) {
                        const m = moment(value);

                        if (m.isValid()) {
                            return (scope.maxDate = m.toDate());
                        }
                    }

                    scope.maxDate = null;
                }

                // Copy year, month, and date onto valueDate
                function _onDateChanged() {
                    if (scope.valueDate && scope.date) {
                        // some month have 31 days, some have 30, set date first can avoid month overflow problem
                        // e.g. 2017-10-31 -> 2017-11-01
                        // it update the month first it will be come 2017-11-31 which isn't exist,
                        // then it will goes to 2017-12-01
                        // so prefer to set date first which it will become 2017-10-01 then 2017-11-01.
                        // but it will cause a issue if the date is 2017-11-30 and going to select 2017-10-31.
                        // because 2017-11-31 doesn't exist when date update first, it will become 2017-11-01 then 2017-10-01.
                        // so Date need to be set once more.
                        scope.valueDate.setFullYear(scope.date.getFullYear());
                        scope.valueDate.setDate(scope.date.getDate());
                        scope.valueDate.setMonth(scope.date.getMonth());
                        scope.valueDate.setDate(scope.date.getDate());
                    } else {
                        scope.valueDate = scope.date;
                    }

                    valueDateWatcher(scope.valueDate);

                    if (isFunction(scope.dispatchChange)) {
                        scope.dispatchChange();
                    }
                }

                function valueDateWatcher(newValue) {
                    scope.date = newValue || null;

                    let dateString = null;

                    if (newValue) {
                        // Transform into string
                        const momentInstance = moment(newValue, dateParseFormats());

                        if (momentInstance.isValid()) {
                            dateString = momentInstance.format('YYYY-MM-DD');
                        }
                    }

                    scope.value = dateString;
                }
            },
        },
    };
}
